import { getToken } from "firebase/messaging";
import { messaging, VAPID_KEY } from "./firebase";
import { _put, SERVER_URL } from "./connectionHelpers";
import { store } from "../redux/store/setup";
import idx from "idx";

/**
 * Storage keys used for notification state and FCM tokens in localStorage.
 */
export const NOTIFICATION_STORAGE_KEYS = {
  PERMISSION_ASKED: "notification_permission_asked",
  FCM_TOKEN: "fcm_token",
  FCM_WEB_TOKEN: "fcm_web_token",
  LAST_SYNCED_TOKEN: "last_synced_fcm_token",
};

// Global in-flight tracking & deduplication guards to ensure single execution
let inFlightUpdatePromise = null;
let inFlightPermissionPromise = null;
let inMemoryLastSyncedToken = null;

/**
 * Get current user auth token from Redux store.
 * @returns {string|null}
 */
export const getAuthToken = () => {
  try {
    const state = store?.getState();
    return (
      state?.appReducer?.loginData?.data?.token ||
      state?.appReducer?.loginData?.token ||
      state?.authReducer?.loginData?.data?.token ||
      state?.authReducer?.loginData?.token ||
      null
    );
  } catch (e) {
    return null;
  }
};

/**
 * Check if the browser environment supports the Web Notification API and Firebase Messaging.
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  if (!("Notification" in window)) {
    return false;
  }

  // iOS Safari only supports Web Push Notifications in iOS 16.4+ when added to the home screen as PWA
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone =
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);

  if (isIOS && !isStandalone) {
    return false;
  }

  return true;
};

/**
 * Get current browser notification permission status ('granted' | 'denied' | 'default' | 'unsupported').
 * @returns {string}
 */
export const getNotificationPermissionStatus = () => {
  if (!isNotificationSupported()) {
    return "unsupported";
  }
  return Notification.permission;
};

/**
 * Check if notification permission has already been requested from this user / browser.
 * Stored in localStorage so we don't prompt repeatedly across sessions.
 * @returns {boolean}
 */
export const hasAskedNotificationPermission = () => {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(NOTIFICATION_STORAGE_KEYS.PERMISSION_ASKED) === "true";
  } catch (error) {
    console.error("[NotificationService] Error reading permission flag from localStorage:", error);
    return false;
  }
};

/**
 * Set the flag in localStorage recording that notification permission was requested.
 * @param {boolean} [asked=true]
 */
export const setNotificationPermissionAsked = (asked = true) => {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(NOTIFICATION_STORAGE_KEYS.PERMISSION_ASKED, asked ? "true" : "false");
    }
  } catch (error) {
    console.error("[NotificationService] Error setting permission flag in localStorage:", error);
  }
};

/**
 * Retrieve the cached FCM token from localStorage if present.
 * @returns {string|null}
 */
export const getStoredFcmToken = () => {
  try {
    if (typeof localStorage === "undefined") return null;
    return (
      localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN) ||
      localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_WEB_TOKEN) ||
      null
    );
  } catch (error) {
    console.error("[NotificationService] Error reading FCM token from localStorage:", error);
    return null;
  }
};

/**
 * Persist the FCM registration token into localStorage.
 * @param {string} token
 */
export const setStoredFcmToken = (token) => {
  try {
    if (typeof localStorage !== "undefined" && token) {
      localStorage.setItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN, token);
      localStorage.setItem(NOTIFICATION_STORAGE_KEYS.FCM_WEB_TOKEN, token);
    }
  } catch (error) {
    console.error("[NotificationService] Error setting FCM token in localStorage:", error);
  }
};

/**
 * Retrieve the last FCM token that was successfully synced to the backend.
 * @returns {string|null}
 */
export const getLastSyncedFcmToken = () => {
  try {
    if (typeof localStorage === "undefined") return inMemoryLastSyncedToken;
    return (
      localStorage.getItem(NOTIFICATION_STORAGE_KEYS.LAST_SYNCED_TOKEN) ||
      inMemoryLastSyncedToken
    );
  } catch (e) {
    return inMemoryLastSyncedToken;
  }
};

/**
 * Persist the last synced FCM token in memory and localStorage.
 * @param {string} token
 */
export const setLastSyncedFcmToken = (token) => {
  inMemoryLastSyncedToken = token;
  try {
    if (typeof localStorage !== "undefined" && token) {
      localStorage.setItem(NOTIFICATION_STORAGE_KEYS.LAST_SYNCED_TOKEN, token);
    }
  } catch (e) {
    console.error("[NotificationService] Error storing last synced FCM token:", e);
  }
};

/**
 * Remove stored FCM registration token(s) from localStorage (e.g. on logout).
 */
export const clearStoredFcmToken = () => {
  inMemoryLastSyncedToken = null;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
      localStorage.removeItem(NOTIFICATION_STORAGE_KEYS.FCM_WEB_TOKEN);
      localStorage.removeItem(NOTIFICATION_STORAGE_KEYS.LAST_SYNCED_TOKEN);
    }
  } catch (error) {
    console.error("[NotificationService] Error clearing FCM token from localStorage:", error);
  }
};

/**
 * Ensure a functional service worker registration is active before calling getToken.
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export const getActiveServiceWorkerRegistration = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  try {
    let reg = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    if (!reg) {
      reg = await navigator.serviceWorker.getRegistration();
    }
    if (!reg) {
      reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
        scope: "/",
        updateViaCache: "none",
      });
    }

    if (reg.installing) {
      await new Promise((resolve) => {
        reg.installing.addEventListener("statechange", (e) => {
          if (e.target.state === "activated") resolve();
        });
        setTimeout(resolve, 3000);
      });
    }

    const readyReg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((res) => setTimeout(() => res(reg), 3000)),
    ]);

    return readyReg || reg;
  } catch (e) {
    console.warn("[NotificationService] Error getting service worker registration:", e);
    return null;
  }
};

/**
 * Robust FCM token fetcher with serviceWorker ready check & retry
 * @returns {Promise<string|null>}
 */
export const fetchFcmToken = async () => {
  try {
    const swReg = await getActiveServiceWorkerRegistration();
    const options = { vapidKey: VAPID_KEY };
    if (swReg) {
      options.serviceWorkerRegistration = swReg;
    }
    console.log("[NotificationService] Fetching FCM token with options:", options);
    const token = await getToken(messaging, options);
    console.log("[NotificationService] FCM token received successfully:", token);
    return token;
  } catch (err) {
    console.warn("[NotificationService] First attempt to get FCM token failed, retrying in 1.2s...", err);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    try {
      const swReg = await getActiveServiceWorkerRegistration();
      const options = { vapidKey: VAPID_KEY };
      if (swReg) {
        options.serviceWorkerRegistration = swReg;
      }
      const token = await getToken(messaging, options);
      console.log("[NotificationService] FCM token received on retry:", token);
      return token;
    } catch (retryErr) {
      console.error("[NotificationService] Failed to retrieve FCM token after retry:", retryErr);
      return null;
    }
  }
};

/**
 * Update the FCM web registration token on the backend server for the authenticated user.
 * Deduplicates calls: if this exact token is already in-flight or was already successfully synced,
 * the duplicate network request is avoided.
 *
 * @param {string} token
 * @param {string} [explicitAuthToken]
 * @param {boolean} [force=false]
 * @returns {Promise<any>}
 */
export const updateFcmTokenOnServer = (token, explicitAuthToken, force = false) => {
  if (!token) {
    console.warn("[NotificationService] updateFcmTokenOnServer called without token");
    return Promise.resolve(null);
  }

  // Deduplication: If already successfully synced to backend and not forcing, skip duplicate network call
  const syncedToken = getLastSyncedFcmToken();
  if (!force && syncedToken === token) {
    console.log("[NotificationService] FCM token already synchronized with backend, skipping duplicate API call.");
    return Promise.resolve({ alreadySynced: true, token });
  }

  // Deduplication: If an update is currently in flight, return the pending promise instead of creating another call
  if (inFlightUpdatePromise) {
    console.log("[NotificationService] FCM token update is already in flight, returning existing promise.");
    return inFlightUpdatePromise;
  }

  inFlightUpdatePromise = new Promise((resolve) => {
    try {
      const authToken = explicitAuthToken || getAuthToken();
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      const payload = {
        fcm_token: token,
      };

      console.log("[NotificationService] Calling PUT /update-fcm-token with payload:", payload);

      _put(
        `${SERVER_URL}/update-fcm-token`,
        headers,
        JSON.stringify(payload),
        (res) => {
          console.log("[NotificationService] FCM token successfully updated on server:", res);
          setLastSyncedFcmToken(token);
          resolve(res);
        },
        (err) => {
          console.warn("[NotificationService] FCM token update failed on server:", err);
          resolve(null);
        }
      );
    } catch (e) {
      console.warn("[NotificationService] Exception while sending FCM token to server:", e);
      resolve(null);
    }
  }).finally(() => {
    inFlightUpdatePromise = null;
  });

  return inFlightUpdatePromise;
};

/**
 * Request notification permission from the user if not asked before (or if forced).
 * If granted, fetches the FCM registration token, persists it in localStorage,
 * and synchronizes it with the backend server.
 * Uses an in-flight promise lock so concurrent calls (e.g. from rapid component renders)
 * resolve through a single execution rather than multiple duplicate prompts/requests.
 *
 * @param {Object} [options]
 * @param {boolean} [options.force=false] - Whether to bypass the hasAskedNotificationPermission check.
 * @param {string} [options.authToken] - Explicit user auth token.
 * @returns {Promise<string|null>} The token if granted and retrieved, or null otherwise.
 */
export const askNotificationPermission = async ({ force = false, authToken: explicitToken } = {}) => {
  if (!isNotificationSupported()) {
    return null;
  }

  // If a permission request or registration flow is already in flight, reuse the promise
  if (inFlightPermissionPromise) {
    console.log("[NotificationService] askNotificationPermission already in flight, returning existing promise.");
    return inFlightPermissionPromise;
  }

  inFlightPermissionPromise = (async () => {
    const currentPermission = Notification.permission;
    console.log("[NotificationService] Current notification permission:", currentPermission);

    // If denied, the browser has blocked notifications for this origin; cannot popup
    if (currentPermission === "denied") {
      return null;
    }

    const authToken = explicitToken || getAuthToken();

    // If already granted, sync token without prompting
    if (currentPermission === "granted") {
      if (!authToken) {
        console.log("[NotificationService] Permission granted, but waiting for auth token to sync with server.");
        return null;
      }
      let storedToken = getStoredFcmToken();
      if (!storedToken) {
        const token = await fetchFcmToken();
        if (token) {
          setStoredFcmToken(token);
          setNotificationPermissionAsked(true);
          await updateFcmTokenOnServer(token, authToken, force);
          return token;
        }
      } else {
        await updateFcmTokenOnServer(storedToken, authToken, force);
        return storedToken;
      }
      return storedToken;
    }

    // Whenever Notification.permission is "default", popup for notification permission!
    if (currentPermission === "default") {
      try {
        console.log("[NotificationService] Prompting for notification permission...");
        const permission = await Notification.requestPermission();
        console.log("[NotificationService] Notification permission response:", permission);

        if (permission === "granted") {
          setNotificationPermissionAsked(true);
          const token = await fetchFcmToken();
          if (token) {
            setStoredFcmToken(token);
            const tokenToUse = authToken || getAuthToken();
            await updateFcmTokenOnServer(token, tokenToUse, force);
            return token;
          } else {
            console.warn("[NotificationService] Permission granted, but FCM token could not be retrieved.");
          }
        } else if (permission === "denied") {
          setNotificationPermissionAsked(true);
        }
      } catch (error) {
        console.error("[NotificationService] Error requesting notification permission:", error);
      }
    }

    return null;
  })().finally(() => {
    inFlightPermissionPromise = null;
  });

  return inFlightPermissionPromise;
};
